import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  Switch,
  tokens,
} from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  type AttributeDefinition,
  catalogue,
  type VariantPreviewRow,
  type VariantQuantity,
} from "../api/catalogue";
import { ApiError } from "../api/client";
import { baeTypography } from "../theme/baeTheme";
import { PHONE } from "../ui/tokens";

const useStyles = makeStyles({
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  // Wide enough for a colour name, narrow enough that a phone still fits two across.
  choices: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(8.5rem, 1fr))",
    gap: tokens.spacingHorizontalXS,
  },
  quickRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "end",
    gap: tokens.spacingHorizontalM,
  },
  grid: { display: "grid", gap: tokens.spacingVerticalXS, minWidth: 0 },
  // Label left, quantity right. The quantity column is fixed so the boxes line up into a column
  // your thumb can run down without hunting.
  line: {
    display: "grid",
    gridTemplateColumns: "1fr 6rem",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    minWidth: 0,
  },
  lineLabel: { display: "grid", gap: "1px", minWidth: 0 },
  name: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sku: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "11px" },
  muted: { color: tokens.colorNeutralForeground3 },
  totals: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
});

/** What the caller holds on to: which values are ticked, and how many of each combination. */
export type VariantChoice = {
  /** Ticked values per axis, keyed by attribute definition id. */
  selections: Record<string, string[]>;
  /** Quantity per combination, keyed by the SKU suffix the API generated. */
  quantities: Record<string, string>;
};

export const emptyVariantChoice: VariantChoice = { selections: {}, quantities: {} };

/**
 * How many things one design may vary along.
 *
 * Mirrors the API, which is the authority and refuses a fourth. Three is where Shopify caps as
 * well, so a fourth axis would build SKUs that could never be published to the first sales
 * channel this connects to.
 */
export const MAX_VARIANT_AXES = 3;

/**
 * Turns "does this come in colours and sizes?" into a list of SKUs with a count against each.
 *
 * The complexity of a variant matrix is real, so it is kept where it belongs — in the API, which
 * works out the combinations and composes each SKU. What the team sees is two questions: tick the
 * colours you have, tick the sizes, then say how many of each. Nothing here decides what a SKU
 * looks like (ADR-0009); it renders the plan the API returns and sends counts back against it.
 */
export function VariantPicker({
  categoryId,
  axes,
  enabled,
  value,
  onToggle,
  onChange,
}: {
  categoryId: string;
  /** The tenant fields the owner marked as making SKUs, already scoped to the category. */
  axes: AttributeDefinition[];
  enabled: boolean;
  value: VariantChoice;
  onToggle: (enabled: boolean) => void;
  onChange: (next: VariantChoice) => void;
}) {
  const styles = useStyles();

  const chosenFor = (axisId: string) => value.selections[axisId] ?? [];

  const preview = useVariantPlan(categoryId, axes, value.selections, enabled);
  const ready = preview.ready;
  const combinations = preview.combinations;

  const toggleValue = (axisId: string, optionValue: string, ticked: boolean) => {
    const current = chosenFor(axisId);
    const next = ticked
      ? [...current, optionValue]
      : current.filter((entry) => entry !== optionValue);

    onChange({
      ...value,
      selections: { ...value.selections, [axisId]: next },
      // Quantities are keyed by a suffix that only makes sense for the plan they were typed
      // against. Un-ticking a colour changes the plan, so anything typed for a combination that no
      // longer exists is dropped rather than silently applied to a different SKU.
      quantities: value.quantities,
    });
  };

  const setAll = (quantity: string) =>
    onChange({
      ...value,
      quantities: Object.fromEntries(combinations.map((row) => [row.skuSuffix, quantity])),
    });

  const quantityFor = (row: VariantPreviewRow) => value.quantities[row.skuSuffix] ?? "";

  const total = combinations.reduce((running, row) => running + (Number(quantityFor(row)) || 0), 0);

  // Phrased to stay singular throughout, in the owner's own words for the field. Pluralising is
  // not safe here: the axis name is whatever the tenant typed, and "Finish" would become
  // "Finishs" while "Category" would become "Categorys".
  const question = `Does this come in more than one ${axes
    .map((axis) => axis.name.toLowerCase())
    .join(" or ")}?`;

  if (axes.length === 0) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <Switch label={question} checked={enabled} onChange={(_, d) => onToggle(d.checked)} />

      {!enabled ? (
        <Caption1 className={styles.muted}>
          Leave this off for a one-off piece — it gets a single SKU and a single count.
        </Caption1>
      ) : (
        <>
          {axes.map((axis) => (
            <Field
              key={axis.id}
              label={`Which ${axis.name.toLowerCase()} does it come in?`}
              hint={axis.hint ?? undefined}
            >
              <div className={styles.choices}>
                {axis.options.map((option) => (
                  <Checkbox
                    key={option.value}
                    label={option.label}
                    checked={chosenFor(axis.id).includes(option.value)}
                    onChange={(_, d) => toggleValue(axis.id, option.value, Boolean(d.checked))}
                  />
                ))}
              </div>
            </Field>
          ))}

          {preview.error instanceof ApiError && (
            <MessageBar intent="error">
              <MessageBarBody>{preview.error.message}</MessageBarBody>
            </MessageBar>
          )}

          {!ready ? (
            <Caption1 className={styles.muted}>
              Tick at least one of each to see the SKUs this makes.
            </Caption1>
          ) : preview.isFetching ? (
            <Spinner size="tiny" label="Working out the SKUs" />
          ) : combinations.length > 0 ? (
            <>
              <div className={styles.quickRow}>
                <Body1Strong>How many of each?</Body1Strong>
                <Field label="Same for all">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    placeholder="e.g. 3"
                    onChange={(_, d) => setAll(d.value)}
                  />
                </Field>
                <Button appearance="subtle" onClick={() => setAll("")}>
                  Clear
                </Button>
              </div>

              <Caption1 className={styles.muted}>
                Fill one number in above to set them all, then change the odd ones out. A SKU left
                at zero is still created — you just have none of it yet.
              </Caption1>

              <div className={styles.grid}>
                {combinations.map((row) => (
                  <div key={row.skuSuffix} className={styles.line}>
                    <div className={styles.lineLabel}>
                      <span className={styles.name}>{row.label}</span>
                      <span className={`${styles.sku} ${styles.muted}`}>…-{row.skuSuffix}</span>
                    </div>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={quantityFor(row)}
                      placeholder="0"
                      onChange={(_, d) =>
                        onChange({
                          ...value,
                          quantities: { ...value.quantities, [row.skuSuffix]: d.value },
                        })
                      }
                    />
                  </div>
                ))}
              </div>

              <div className={styles.totals}>
                <Badge appearance="filled" color="brand">
                  {combinations.length} SKUs
                </Badge>
                <Badge appearance="tint" color="success">
                  {total} pieces
                </Badge>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Asks the API what SKUs the ticked values would make.
 *
 * A hook rather than a query inside the picker, because the screen that saves needs the same plan
 * the screen that draws it is showing. Both call this with the same arguments, so they share one
 * cached request and cannot disagree about which SKUs are about to be created.
 */
export function useVariantPlan(
  categoryId: string,
  axes: AttributeDefinition[],
  selections: Record<string, string[]>,
  enabled: boolean,
) {
  // Every axis needs at least one value before there is anything to ask for. Sent in the order the
  // owner arranged the fields, which is the order the SKU reads in.
  const requestedAxes = useMemo(
    () =>
      axes.map((axis) => ({
        attributeDefinitionId: axis.id,
        values: selections[axis.id] ?? [],
      })),
    [axes, selections],
  );

  const ready =
    enabled && requestedAxes.length > 0 && requestedAxes.every((axis) => axis.values.length > 0);

  const query = useQuery({
    queryKey: ["variant-preview", categoryId, requestedAxes],
    queryFn: () => catalogue.variantPreview(categoryId, requestedAxes),
    enabled: ready,
  });

  return {
    ready,
    axes: requestedAxes,
    combinations: ready ? (query.data?.combinations ?? []) : [],
    isFetching: query.isFetching,
    error: query.error,
  };
}

/**
 * The quantity lines to send.
 *
 * Built from the plan rather than from what was typed, so a number entered against a combination
 * that has since been un-ticked is dropped instead of being applied to whichever SKU inherited its
 * position.
 */
export function variantQuantitiesFrom(
  value: VariantChoice,
  combinations: VariantPreviewRow[],
): VariantQuantity[] {
  return combinations.map((row) => ({
    skuSuffix: row.skuSuffix,
    quantity: Number(value.quantities[row.skuSuffix]) || 0,
    purchasePrice: null,
    sellingPrice: null,
  }));
}
